import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { 
  CheckCircle, 
  AlertTriangle, 
  XCircle, 
  Info,
  Zap,
  ExternalLink,
  Clock
} from 'lucide-react';

interface Issue {
  id: number;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  url: string;
  element: string;
  recommendation: string;
  status: 'pending' | 'fixed' | 'ignored';
  created_at: string;
}

interface IssuesListProps {
  issues: Issue[];
  onFixSingle: (issueId: number) => void;
  isFixing: boolean;
}

const IssuesList: React.FC<IssuesListProps> = ({ issues, onFixSingle, isFixing }) => {
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'medium':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'low':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'fixed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'ignored':
        return <XCircle className="h-4 w-4 text-gray-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'fixed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ignored':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityCount = (severity: string) => {
    return issues.filter(issue => issue.severity === severity).length;
  };

  const severityCounts = {
    critical: getSeverityCount('critical'),
    high: getSeverityCount('high'),
    medium: getSeverityCount('medium'),
    low: getSeverityCount('low'),
    info: getSeverityCount('info')
  };

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {Object.entries(severityCounts).map(([severity, count]) => (
          <Card key={severity} className="p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              {getSeverityIcon(severity)}
            </div>
            <div className="text-2xl font-bold">{count}</div>
            <div className="text-sm text-muted-foreground capitalize">{severity}</div>
          </Card>
        ))}
      </div>

      {/* Issues List */}
      <div className="space-y-4">
        {issues.map((issue) => (
          <Card key={issue.id} className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-3">
                {/* Header */}
                <div className="flex items-center gap-3">
                  {getSeverityIcon(issue.severity)}
                  <h3 className="text-lg font-semibold">{issue.title}</h3>
                  <Badge className={getSeverityColor(issue.severity)}>
                    {issue.severity.toUpperCase()}
                  </Badge>
                  <Badge className={getStatusColor(issue.status)}>
                    {getStatusIcon(issue.status)}
                    <span className="ml-1">{issue.status.toUpperCase()}</span>
                  </Badge>
                </div>

                {/* Description */}
                <p className="text-muted-foreground">{issue.description}</p>

                {/* Details */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium">Category:</span> {issue.category}
                  </div>
                  <div>
                    <span className="font-medium">Element:</span> {issue.element}
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-medium">URL:</span>
                    <a 
                      href={issue.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1"
                    >
                      View Page
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>

                {/* Recommendation */}
                <div className="bg-muted/50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">AI Recommendation:</h4>
                  <p className="text-sm">{issue.recommendation}</p>
                </div>
              </div>

              {/* Fix Button */}
              <div className="ml-4">
                {issue.status === 'pending' ? (
                  <Button
                    onClick={() => onFixSingle(issue.id)}
                    disabled={isFixing}
                    size="sm"
                    className="bg-primary hover:bg-primary/90"
                  >
                    <Zap className="h-4 w-4 mr-2" />
                    Fix with AI
                  </Button>
                ) : issue.status === 'fixed' ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">Fixed</span>
                  </div>
                ) : (
                  <div className="flex items-center text-gray-500">
                    <XCircle className="h-4 w-4 mr-2" />
                    <span className="text-sm">Ignored</span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {issues.length === 0 && (
        <Card className="p-8 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Issues Found!</h3>
          <p className="text-muted-foreground">
            Your website is in excellent condition. Great job!
          </p>
        </Card>
      )}
    </div>
  );
};

export default IssuesList;
